import { SearchSelect } from "@result/rui/components";
import { ApiPage, ApiSort } from "@result/rui/utils/api";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { mergeOptionsWithSelected } from "#/components/SelectBreederCard";
import * as DLD from "#/def/data-layer";
import { useDataLayerQuery, useDataLayerRead } from "#/hooks/useDataLayer";
import * as species from "#/utils/SpeciesScope";

/**
 * Label function for breed options in the search select.
 *
 * @param {object} item
 * @returns string
 */
function optionLabel(item) {
  return (
    [item?.sifra, item?.naziv, item?.species].filter(Boolean).join(" - ") ||
    item?.id
  );
}

/**
 * Search select component for breeds, with filtering to relevant species and custom option labels.
 *
 * @param selectedId
 * @param formField
 * @param onSelection
 * @param isDisabled
 * @param pageSize
 * @param {object} props
 * @param {string[]} [props.allowedSifre] - optional whitelist of breed codes (sifra), e.g. ["OX", "XX", "LIP"]
 * @returns
 */
export default function BreedSearchSelect({
  selectedId = null,
  formField,
  onSelection,
  isDisabled = false,
  pageSize = 10,
  allowedSifre = null,
  ...props
}) {
  const { t } = useTranslation();
  const resolvedSelectedId =
    selectedId ??
    (typeof formField?.value === "string"
      ? formField.value
      : (formField?.value?.id ?? null));

  const {
    result,
    isLoading: isLoadingOptions,
    query,
  } = useDataLayerQuery({
    ident: DLD.BREEDS,
    mode: "offline",
  });
  const { item: selectedBreed, read } = useDataLayerRead({
    ident: DLD.BREEDS,
    mode: "offline",
  });

  useEffect(() => {
    if (!resolvedSelectedId) {
      return;
    }

    read(resolvedSelectedId).catch(() => undefined);
  }, [read, resolvedSelectedId]);

  const allowedSifreSet = useMemo(() => {
    if (!allowedSifre?.length) {
      return null;
    }

    return new Set(allowedSifre.map((sifra) => sifra.toUpperCase()));
  }, [allowedSifre]);

  const mergedOptions = useMemo(() => {
    const options = mergeOptionsWithSelected(
      result?.content || [],
      selectedBreed
    );

    if (!allowedSifreSet) {
      return options;
    }

    return options.filter((breed) =>
      allowedSifreSet.has((breed?.sifra ?? "").toUpperCase())
    );
  }, [allowedSifreSet, result?.content, selectedBreed]);

  const fetchData = useCallback(
    (page, queryString, sort) => {
      const filter = species.filter().and((and) => {
        and.equal("status", "AKTIVNA");
        and.equal("disabled", false);

        if (allowedSifre?.length) {
          and.in("sifra", allowedSifre);
        }
      });

      const trimmedQuery = queryString?.trim();
      if (trimmedQuery) {
        filter.and((andGroup) => {
          andGroup.or((orGroup) => {
            orGroup.likeCaseInsensitive("sifra", trimmedQuery);
            orGroup.likeCaseInsensitive("naziv", trimmedQuery);
          });
        });
      }

      return query({
        page: page ?? new ApiPage({ pageNumber: 0, pageSize }),
        filter,
        sort: sort || new ApiSort().asc("naziv"),
      });
    },
    [allowedSifre, pageSize, query]
  );

  return (
    <SearchSelect
      formField={formField}
      label={t("breed")}
      isDisabled={isDisabled}
      optionId="id"
      optionLabel={optionLabel}
      getOptionsState={() => mergedOptions}
      getLoadingState={() => isLoadingOptions}
      getPageNumberState={() => result?.number ?? 0}
      onFetchData={fetchData}
      onSelection={onSelection}
      {...props}
    />
  );
}

BreedSearchSelect.propTypes = {
  formField: PropTypes.shape({
    value: PropTypes.any,
  }).isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelection: PropTypes.func,
  isDisabled: PropTypes.bool,
  pageSize: PropTypes.number,
  allowedSifre: PropTypes.arrayOf(PropTypes.string),
  ...SearchSelect.propTypes,
  getPageNumberState: PropTypes.func,
  onFetchData: PropTypes.func,
  getLoadingState: PropTypes.func,
  getOptionsState: PropTypes.func,
};
